<template>
  <div id="wrapper">
    <div class="user-filter">
      <div
          :class="{ selected: selectedUserIds.length === 0 }"
          @click="showAllUsers"
          class="user-filter-item"
      >
        Pokaži vse delavce
      </div>
      <div
          v-for="user in usersData"
          :key="user.id"
          :class="['user-filter-item', { selected: selectedUserIds.includes(user.id) }]"
          @click="toggleUserFilter(user.id)"
      >
        {{ user.text }}
      </div>
    </div>

    <DxScheduler
        id="scheduler"
        ref="scheduler"
        :data-source="dataSource"
        :current-date="currentDate"
        :views="views"
        :max-appointments-per-cell="'unlimited'"
        :cell-duration="60"
        :groups="groups"
        :time-zone="'Europe/Ljubljana'"
        :start-day-hour="8"
        :end-day-hour="16"
        :first-day-of-week="1"
        :show-current-time-indicator="true"
        current-view="timelineWeek"
        :current-view.sync="currentView"
        text-expr="name"
        start-date-expr="started_at"
        end-date-expr="completed_at"
        appointment-min-size="100"
        :editing="false"
        :on-option-changed="onOptionChanged"
        appointment-template="AppointmentTemplateSlot"
    >
      <template #dataCellTemplate="{ data: cellData }">
        <DataCell
            :cell-data="cellData"
            :non-working-cells="nonWorkingCellsSet"
        />
      </template>

      <template #AppointmentTemplateSlot="{ data }">
        <div class="dx-item-content dx-scheduler-appointment-content"
             :class="data.appointmentData.completed ? '' : 'incomplete-task'"
             :id="'task-' + data.appointmentData.id"
             :data-id="data.appointmentData.id"
             :data-work-process-id="data.appointmentData.work_processes_id"
             :data-color-code="getTaskColor(data.appointmentData.id)"
             :style="{ 'background-color': getTaskColor(data.appointmentData.id) }"
             :title="data.appointmentData.display_text"
        >
          <div class="dx-scheduler-appointment-title">{{ data.appointmentData.display_text }}</div>
          <div class="dx-scheduler-appointment-content-details">
            <div class="dx-scheduler-appointment-content-date">
              {{ formatTime(data.appointmentData.started_at) }}
              -
              {{ formatTime(data.appointmentData.completed_at) }}
            </div>
          </div>
        </div>
      </template>
      <DxResource
          :allow-multiple="false"
          :data-source="filteredUsersData"
          field-expr="default_user_id"
          label="Zaposleni"
      />
      <svg ref="connectionsSvg" class="connections-svg"></svg>
    </DxScheduler>
    <vue-toastr ref="toastr"></vue-toastr>
  </div>
</template>

<script>
import DataCell from "@/views/components/WorkUtilizationDataCell.vue";
import {DxScheduler, DxResource} from 'devextreme-vue/scheduler';
import UsersService from "@/services/users";
import SchedulerService from "@/services/scheduler";
import axios from "axios";
import CustomStore from "devextreme/data/custom_store";
import {locale, loadMessages} from "devextreme/localization";
import {format} from 'date-fns'

export default {
  name: 'WeeklyOverview',
  components: {
    DxScheduler,
    DxResource,
    DataCell
  },
  data: function () {
    return {
      views: ['timelineWeek'],
      groups: ['default_user_id'],
      currentDate: new Date(),
      currentView: 'timelineWeek',
      scheduledTasks: [],
      dataSource: new CustomStore({
        key: "id",
        load: async () => {
          try {
            const dateFrom = this.formatDate(this.$refs.scheduler.instance.getStartViewDate());
            const dateTo = this.formatDate(this.$refs.scheduler.instance.getEndViewDate());

            const params = {
              date_from: dateFrom + ' 00:00:00',
              date_to: dateTo + ' 23:59:59',
            };

            if (this.selectedUserIds.length > 0) {
              params.user_ids = this.selectedUserIds;
            }

            // Fetch working time setups
            try {
              if (this.usersData.length > 0) {
                const timesheetParams = {
                  start_date: dateFrom,
                  end_date: dateTo,
                  user_ids: this.usersData.map(user => user.id)
                };
                const timesheetResponse = await SchedulerService.getTimesheetForPeriod(timesheetParams);
                this.timesheetData = timesheetResponse.timesheet || [];

                // Update scheduler hours after timesheet is loaded
                this.updateSchedulerHours();
              } else {
                this.timesheetData = [];
              }
            } catch (timesheetError) {
              this.timesheetData = [];
            }

            const response = await axios.get("/tasks/filter", { params });
            this.scheduledTasks = response.data.tasks.map(task => this.parseTask(task));

            return this.scheduledTasks;
          } catch (error) {
            console.error("Error loading events:", error);
            this.$refs.toastr.e(error?.response?.data?.error || error.message || "Med nalaganjem nalog je prišlo do napake.");
            throw error;
          }
        },
      }),
      usersData: [],
      selectedUserIds: [],
      timesheetData: [],
      updateSchedulerHoursTimeout: null,
    };
  },
  computed: {
    schedulerCellDuration() {
      return 60;
    },
    filteredUsersData() {
      return this.selectedUserIds.length === 0
          ? this.usersData
          : this.usersData.filter(user => this.selectedUserIds.includes(user.id));
    },
    perDayWorkingHours() {
      const dayMap = {};

      if (!this.timesheetData || this.timesheetData.length === 0) {
        return dayMap;
      }

      this.timesheetData.forEach(entry => {
        if (!entry.date) return;

        const dateKey = entry.date;

        if (!dayMap[dateKey]) {
          dayMap[dateKey] = { minHour: 24, maxHour: 0, userHours: {} };
        }

        if (!dayMap[dateKey].userHours[entry.user_id]) {
          dayMap[dateKey].userHours[entry.user_id] = { minHour: 24, maxHour: 0 };
        }

        if (entry.start) {
          const startDate = new Date(entry.start);
          const startHour = startDate.getHours() + (startDate.getMinutes() / 60);

          if (startHour < dayMap[dateKey].minHour) {
            dayMap[dateKey].minHour = startHour;
          }
          if (startHour < dayMap[dateKey].userHours[entry.user_id].minHour) {
            dayMap[dateKey].userHours[entry.user_id].minHour = startHour;
          }
        }

        if (entry.end) {
          const endDate = new Date(entry.end);
          const endHour = endDate.getHours() + (endDate.getMinutes() / 60);

          if (endHour > dayMap[dateKey].maxHour) {
            dayMap[dateKey].maxHour = endHour;
          }
          if (endHour > dayMap[dateKey].userHours[entry.user_id].maxHour) {
            dayMap[dateKey].userHours[entry.user_id].maxHour = endHour;
          }
        }
      });

      return dayMap;
    },
    dynamicStartDayHour() {
      if (!this.timesheetData || this.timesheetData.length === 0) {
        return 8;
      }

      if (this.currentView === 'timelineDay') {
        const dateKey = this.formatDate(this.$refs.scheduler.instance.getStartViewDate());
        const dayHours = this.perDayWorkingHours[dateKey];

        if (dayHours && dayHours.minHour < 24) {
          return Math.floor(dayHours.minHour);
        }
        return 8;
      } else {
        let minHour = 8;

        Object.values(this.perDayWorkingHours).forEach(dayData => {
          if (dayData.minHour < minHour) {
            minHour = dayData.minHour;
          }
        });

        return Math.floor(minHour);
      }
    },
    dynamicEndDayHour() {
      if (!this.timesheetData || this.timesheetData.length === 0) {
        return 16;
      }

      if (this.currentView === 'timelineDay') {
        const dateKey = this.formatDate(this.$refs.scheduler.instance.getStartViewDate());
        const dayHours = this.perDayWorkingHours[dateKey];

        if (dayHours && dayHours.maxHour > 0) {
          return Math.ceil(dayHours.maxHour);
        }
        return 16;
      } else {
        let maxHour = 16;

        Object.values(this.perDayWorkingHours).forEach(dayData => {
          if (dayData.maxHour > maxHour) {
            maxHour = dayData.maxHour;
          }
        });

        return Math.ceil(maxHour);
      }
    },
    nonWorkingCellsSet() {
      const set = new Set();

      if (!this.timesheetData || this.timesheetData.length === 0) {
        return set;
      }

      const scheduler = this.$refs.scheduler?.instance;
      if (!scheduler) return set;

      const startViewDate = scheduler.getStartViewDate();
      const endViewDate = scheduler.getEndViewDate();

      // Get all visible users (considering filter)
      const visibleUsers = this.selectedUserIds.length > 0
          ? this.usersData.filter(user => this.selectedUserIds.includes(user.id))
          : this.usersData;

      // Pre-calculate all non-working cells for visible date range
      visibleUsers.forEach(user => {
        let currentDate = new Date(startViewDate);

        while (currentDate <= endViewDate) {
          const dateKey = this.formatDate(currentDate);

          // Find timesheet entry for this user and date
          const timesheetEntry = this.timesheetData.find(
              entry => entry.user_id === user.id && entry.date === dateKey
          );

          if (!timesheetEntry) {
            // Entire day is non-working - mark all hours
            for (let hour = 0; hour < 24; hour += (this.schedulerCellDuration / 60)) {
              const key = `${user.id}|${dateKey}|${hour}`;
              set.add(key);
            }
          } else {
            // Mark hours outside working time
            const startHour = this.getHourFromTimestamp(timesheetEntry.start);
            const endHour = this.getHourFromTimestamp(timesheetEntry.end);

            for (let hour = 0; hour < 24; hour += (this.schedulerCellDuration / 60)) {
              if (hour < startHour || hour >= endHour) {
                const key = `${user.id}|${dateKey}|${hour}`;
                set.add(key);
              }
            }
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }
      });

      return set;
    }
  },
  async mounted() {
    await this.loadUsers();
    this.scrollToCurrentTime();
  },
  created() {
    this.translateSchedulerToSlovenian();
  },
  methods: {
    formatDate(date) {
      return format(new Date(date), 'yyyy-MM-dd');
    },
    formatTime(dateTime) {
      return format(new Date(dateTime), 'HH:mm');
    },
    getHourFromTimestamp(timestamp) {
      if (!timestamp) return 0;
      const date = new Date(timestamp);
      return date.getHours() + (date.getMinutes() / 60);
    },
    parseTask(task) {
      let spOrdersDataDisplayText = '';
      let spOrdersData = [];
      if (task.work_process.sp_orders.length > 0) {
        for (let i=0; i<task.work_process.sp_orders.length; i++) {
          const spOrder = task.work_process.sp_orders[i];
          const spOrderNumber = spOrder?.sp_order_number ?? "/";
          const spOrderClientFirstName = spOrder?.client?.first_name ?? "";
          const spOrderClientLastName = spOrder?.client?.last_name ?? "";
          const spOrderClientName = spOrderClientFirstName || spOrderClientLastName ? `${spOrderClientFirstName} ${spOrderClientLastName}` : "/";
          const spOrderVehicleDataBrand = spOrder?.vehicle?.brand?.title ?? spOrder?.brand?.title ?? "";
          const spOrderVehicleDataModel = spOrder?.vehicle?.model ?? spOrder?.model ?? "";
          const spOrderVehicleDataRegistrationNumber = spOrder?.vehicle?.registration_number ?? spOrder?.registration_number ?? "";
          const spOrderVehicleData = (spOrderVehicleDataBrand || spOrderVehicleDataModel || spOrderVehicleDataRegistrationNumber) ? `${spOrderVehicleDataBrand} ${spOrderVehicleDataModel} ${spOrderVehicleDataRegistrationNumber}` : "/";

          const spOrderData = {
            id: spOrder.id,
            number: spOrderNumber,
            client_name: spOrderClientName,
            vehicle_data: spOrderVehicleData
          };
          spOrdersDataDisplayText += `Naročilo: ${spOrderData.number}`;
          spOrdersDataDisplayText += " | ";
          spOrdersDataDisplayText += `Stranka: ${spOrderData.client_name}`;
          spOrdersDataDisplayText += " | ";
          spOrdersDataDisplayText += `Vozilo: ${spOrderData.vehicle_data}`;
          spOrdersData.push(spOrderData);
        }
      }

      let displayText = [];
      if (task.name) {
        displayText.push(task.name);
      }
      if (spOrdersDataDisplayText) {
        displayText.push(spOrdersDataDisplayText);
      }
      if (task.description) {
        displayText.push(task.description);
      }

      return {
        ...task,
        display_text: displayText.join(" | "),
        sp_orders_data: spOrdersData,
      }
    },
    getTaskColor(taskId) {
      const task = this.scheduledTasks.find(task => task.id === taskId);

      if (!task) {
        return null;
      }
      if (task.completed) {
        return '#4CAF50';
      }
      if (!task.completed && new Date(task.completed_at).getTime() < new Date().getTime()) {
        return '#ff9800';
      }
      const overlappingTask = this.scheduledTasks.find(t =>
          t.id !== taskId &&
          t.work_processes_id === task.work_processes_id &&
          t.started_at && t.completed_at &&
          !t.completed &&
          !(new Date(task.started_at) >= new Date(t.completed_at) || new Date(task.completed_at) <= new Date(t.started_at))
      );
      if (overlappingTask) {
        return '#f44336';
      }

      return '#2196F3';
    },
    translateSchedulerToSlovenian() {
      loadMessages({
        "sl": {
          "dxScheduler-editorLabelTitle": "Zadeva",
          "dxScheduler-editorLabelStartDate": "Začetni datum",
          "dxScheduler-editorLabelEndDate": "Končni datum",
          "dxScheduler-editorLabelDescription": "Opis",
          "dxScheduler-timelineDay": "Časovnica - Dan",
          "dxScheduler-timelineWeek": "Časovnica - Teden"
        }
      });
      locale("sl");
    },
    async loadUsers() {
      try {
        const requestData = {
          size: 200,
          page: 1,
          sort: '',
          desc: false,
        };
        const responseData = await UsersService.listAll(requestData);
        this.usersData = responseData.users
            .filter(user => user.roles.some(role => ['licar', 'klepar'].includes(role.name)))
            .map(user => ({
              text: user.name,
              id: user.id,
              color: "transparent",
            }));
      } catch (error) {
        console.error('Error fetching users:', error);
        this.$refs.toastr.e(error?.response?.data?.error || error.message || "Med pridobivanjem uporabnikov je prišlo do napake.");
      }
    },
    scrollToCurrentTime() {
      this.$nextTick(() => {
        const scheduler = this.$refs.scheduler;
        if (!scheduler) return;

        const schedulerInstance = scheduler.instance;
        const scrollable = schedulerInstance.getWorkSpaceScrollable();
        if (!scrollable) return;

        const currentDate = new Date(schedulerInstance.option("currentDate"));
        const firstDayOfWeek = schedulerInstance.option("firstDayOfWeek") || 1;
        const dayOfWeek = currentDate.getDay();
        const daysToSubtract = (dayOfWeek - firstDayOfWeek + 7) % 7;

        const startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - daysToSubtract);
        startDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const daysDifference = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));

        if (daysDifference < 0) return;

        const cellWidth = schedulerInstance.$element()
            .find(".dx-scheduler-date-table-cell")
            .first()
            .outerWidth(true);

        if (!cellWidth) return;

        const scrollLeftPosition = daysDifference * cellWidth;
        scrollable.scrollTo({ left: scrollLeftPosition, behavior: "smooth" });
      });
    },
    scrollToAppropriateDay() {
      this.$nextTick(() => {
        const scheduler = this.$refs.scheduler;
        if (!scheduler) return;

        const schedulerInstance = scheduler.instance;
        const scrollable = schedulerInstance.getWorkSpaceScrollable();
        if (!scrollable) return;

        const currentDate = new Date(schedulerInstance.option("currentDate"));
        const firstDayOfWeek = schedulerInstance.option("firstDayOfWeek") || 1;
        const dayOfWeek = currentDate.getDay();
        const daysToSubtract = (dayOfWeek - firstDayOfWeek + 7) % 7;

        const startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - daysToSubtract);
        startDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startDate);
        endOfWeek.setDate(startDate.getDate() + 6);

        const isCurrentWeek = today >= startDate && today <= endOfWeek;

        let targetDate;
        if (isCurrentWeek) {
          targetDate = today;
        } else {
          targetDate = startDate;
        }

        const daysDifference = Math.floor((targetDate - startDate) / (1000 * 60 * 60 * 24));

        const cellWidth = schedulerInstance.$element()
            .find(".dx-scheduler-date-table-cell")
            .first()
            .outerWidth(true);

        if (!cellWidth) return;

        const scrollLeftPosition = daysDifference * cellWidth;
        scrollable.scrollTo({ left: scrollLeftPosition, behavior: "smooth" });
      });
    },
    updateSchedulerHours() {
      if (this.updateSchedulerHoursTimeout) {
        clearTimeout(this.updateSchedulerHoursTimeout);
      }

      this.updateSchedulerHoursTimeout = setTimeout(() => {
        this.$nextTick(() => {
          const scheduler = this.$refs.scheduler?.instance;
          if (!scheduler) return;

          const scrollContainer = document.querySelector('.dx-scheduler-date-table-scrollable .dx-scrollable-container');
          let savedScrollLeft = 0;
          let savedScrollTop = 0;

          if (scrollContainer) {
            savedScrollLeft = scrollContainer.scrollLeft;
            savedScrollTop = scrollContainer.scrollTop;
          }

          const newStartHour = this.dynamicStartDayHour;
          const newEndHour = this.dynamicEndDayHour;

          scheduler.option('startDayHour', newStartHour);
          scheduler.option('endDayHour', newEndHour);

          scheduler.repaint();

          this.$nextTick(() => {
            const scrollContainerAfter = document.querySelector('.dx-scheduler-date-table-scrollable .dx-scrollable-container');
            if (scrollContainerAfter && (savedScrollLeft !== 0 || savedScrollTop !== 0)) {
              scrollContainerAfter.scrollLeft = savedScrollLeft;
              scrollContainerAfter.scrollTop = savedScrollTop;
            }
          });
        });
      }, 100);
    },
    showAllUsers() {
      this.selectedUserIds = [];
      this.$refs.scheduler.instance.getDataSource().reload();
    },
    toggleUserFilter(userId) {
      const index = this.selectedUserIds.indexOf(userId);
      if (index > -1) {
        this.selectedUserIds.splice(index, 1);
      } else {
        this.selectedUserIds.push(userId);
      }

      this.$refs.scheduler.instance.getDataSource().reload();
    },
    connectElementsWithStraightLine(container, element1, element2) {
      const box1 = element1.getBoundingClientRect();
      const box2 = element2.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      let svg = container.querySelector("svg.connection-lines");
      if (!svg) {
        svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.classList.add("connection-lines");
        svg.style.position = "absolute";
        svg.style.top = "0";
        svg.style.left = "0";
        svg.style.width = "100%";
        svg.style.height = "100%";
        svg.style.pointerEvents = "none";
        svg.style.zIndex = 1;
        container.appendChild(svg);
      }

      const startX = box1.right - containerRect.left;
      const startY = box1.top + box1.height / 2 - containerRect.top;

      const endX = box2.left - containerRect.left;
      const endY = box2.top + box2.height / 2 - containerRect.top;

      let d;

      if (Math.abs(startY - endY) < 3) {
        d = `M ${startX} ${startY} L ${endX} ${endY}`;
      } else {
        const midX1 = startX;
        const midY1 = startY;
        const midX2 = midX1;
        const midY2 = (startY + endY) / 2;
        const midX3 = endX;
        const midY3 = midY2;
        const midX4 = midX3;
        const midY4 = endY;

        d = `
          M ${startX} ${startY}
          C ${startX} ${startY}, ${midX1} ${midY1}, ${midX1} ${midY1}
          L ${midX2} ${midY2}
          C ${midX2} ${midY2}, ${midX3} ${midY3}, ${midX3} ${midY3}
          L ${midX4} ${midY4}
          C ${midX4} ${midY4}, ${endX} ${endY}, ${endX} ${endY}
        `;
      }

      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", d);
      path.setAttribute("stroke", "black");
      path.setAttribute("stroke-width", 2);
      path.setAttribute("fill", "none");
      path.setAttribute("data-work-process-id", element1.getAttribute("data-work-process-id"));
      path.style.opacity = '0.5';

      svg.appendChild(path);
    },
    clearLines(container) {
      const svg = container.querySelector("svg.connection-lines");
      if (svg) {
        svg.innerHTML = "";
      }
    },
    onOptionChanged(e) {
      if (e.name === 'currentView') {
        this.$refs.scheduler.instance.getDataSource().reload().then(() => {
          this.updateSchedulerHours();
        });
        this.scrollToCurrentTime();
      } else if (e.name === 'currentDate') {
        this.updateSchedulerHours();
        this.scrollToAppropriateDay();
      }
    },
  },
};
</script>

<style>
#wrapper {
  position: relative;
  height: 100%;
}
.c-main > .container-fluid {
  height: 100%;
  padding-bottom: 15px;
}
th.dx-scheduler-header-panel-cell.dx-scheduler-cell-sizes-horizontal,
td.dx-scheduler-date-table-cell {
  min-width: 20px;
}
.dx-scheduler-timeline .dx-scheduler-date-table-cell,
.dx-scheduler-timeline .dx-scheduler-header-panel-cell:not(.dx-scheduler-header-panel-week-cell) {
  width: auto !important;
}
th.dx-scheduler-header-panel-cell.dx-scheduler-cell-sizes-horizontal:not(.dx-scheduler-header-panel-week-cell) {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  padding: 5px 2px;
  white-space: nowrap;
}
.dx-scheduler-appointment-content {
  padding: 1px 2px;
  opacity: 0.9;
}
.dx-scheduler-appointment-content .dx-scheduler-appointment-title {
  font-size: 12px;
  line-height: 1.1;
}
.dx-scheduler-appointment-content .dx-scheduler-appointment-content-details {
  margin-top: 0;
  font-size: 10px;
}
.dx-scheduler-timeline.dx-scheduler-work-space-grouped .dx-scheduler-group-row .dx-scheduler-group-header {
  align-items: center;
  font-weight: bold;
  font-size: 13px;
  min-height: 40px !important;
  height: 40px !important;
}
.dx-scheduler-timeline .dx-scheduler-group-header-content {
  line-height: 1.15;
}
.dx-scheduler-work-space.dx-scheduler-timeline .dx-scheduler-date-table-row {
  height: 40px !important;
}
.dx-scheduler-work-space.dx-scheduler-timeline .dx-scheduler-date-table-cell {
  height: 40px !important;
  min-height: 40px !important;
}
.dx-appointment-form-switch.dx-first-row.dx-last-row.dx-field-item.dx-field-item-optional.dx-flex-layout.dx-label-h-align {
  display: none;
}
.dx-tooltip-appointment-item-delete-button-container {
  display: none;
}
.dx-scheduler-appointment {
  z-index: 2;
  padding: 0px 0px;
  border: none !important;
  box-shadow: none !important;
}
.dx-popup-content a {
  color: #63a0d4;
}
th.dx-scheduler-header-panel-cell.dx-scheduler-header-panel-week-cell {
  border-right: 2px solid #63a0d4 !important;
}
th.dx-scheduler-header-panel-cell:not(.dx-scheduler-header-panel-week-cell) {
  font-size: 10px !important;
}
.dx-scheduler-all-day-table-cell.dx-state-hover, .dx-scheduler-date-table-cell.dx-state-hover {
  background-color: rgba(0, 0, 0, 0.2);
}
.non-working-timeslot {
  background-color: #f5f5f5 !important;
  opacity: 0.5 !important;
  pointer-events: none !important;
  position: relative;
  cursor: not-allowed !important;
  background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 5px,
      rgba(0, 0, 0, 0.2) 5px,
      rgba(0, 0, 0, 0.2) 10px
  );
}
.user-filter {
  padding: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  width: 100%;
  font-size: 14px;
}
.user-filter-item {
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  display: inline-block;
}
.user-filter-item:hover {
  background-color: #c1d75145;
}
.user-filter-item.selected {
  background-color: #c1d751;
  color: white;
}
</style>

